// geometry.ts
// Shared polygon math used by area collision (player.ts), rendering (chunk.ts),
// the map editor (areaTool.ts) and pathfinding (pathAi.ts).
import { AreaNode } from './types';

export type Bounds = { minX: number, minY: number, maxX: number, maxY: number };

const EPSILON = 0.0001;

// Ray-casting point-in-polygon test. Works for convex and concave simple polygons.
export function pointInPolygon(px: number, py: number, nodes: AreaNode[]): boolean {
    let inside = false;
    for (let i = 0, j = nodes.length - 1; i < nodes.length; j = i++) {
        const xi = nodes[i].x, yi = nodes[i].y;
        const xj = nodes[j].x, yj = nodes[j].y;

        const intersects = ((yi > py) !== (yj > py)) &&
            (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
        if (intersects) {
            inside = !inside;
        }
    }
    return inside;
}

// Closest point on a single line segment to point (px, py).
function closestPointOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): { x: number, y: number, distSq: number } {
    const abx = bx - ax;
    const aby = by - ay;
    const lenSq = abx * abx + aby * aby;
    let t = lenSq > 0 ? ((px - ax) * abx + (py - ay) * aby) / lenSq : 0;
    t = Math.max(0, Math.min(1, t));
    const x = ax + abx * t;
    const y = ay + aby * t;
    const dx = px - x;
    const dy = py - y;
    return { x, y, distSq: dx * dx + dy * dy };
}

// Signed area of the polygon. Positive = counter-clockwise winding (in standard
// math orientation), negative = clockwise. Used to derive outward edge normals
// regardless of how the polygon's nodes happen to be ordered.
function signedArea(nodes: AreaNode[]): number {
    let area = 0;
    for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const b = nodes[(i + 1) % nodes.length];
        area += a.x * b.y - b.x * a.y;
    }
    return area / 2;
}

// Outward-facing unit normal of the edge from nodes[i] to nodes[(i+1)%n],
// accounting for winding order so it always points away from the polygon interior.
function edgeOutwardNormal(nodes: AreaNode[], i: number, ccw: boolean): { nx: number, ny: number } {
    const a = nodes[i];
    const b = nodes[(i + 1) % nodes.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len = Math.sqrt(ex * ex + ey * ey) || 1;
    // Rotate the edge direction 90 degrees; which way depends on winding order
    // so the result always points outward (away from the polygon interior).
    if (ccw) {
        return { nx: ey / len, ny: -ex / len };
    } else {
        return { nx: -ey / len, ny: ex / len };
    }
}

export type ClosestPolygonPoint = {
    x: number,
    y: number,
    dist: number,
    nx: number, // outward normal at the closest point (blended at shared vertices)
    ny: number
};

// Finds the closest point on the polygon's boundary to (px, py), checking every
// edge and returning the global minimum. When the closest point lands on (or
// very near) a vertex shared by two edges, the outward normals of both edges
// are blended so push-out direction at corners isn't arbitrarily picked from
// whichever edge happened to be checked first.
export function closestPointOnPolygonEdges(px: number, py: number, nodes: AreaNode[]): ClosestPolygonPoint {
    const ccw = signedArea(nodes) > 0;
    let best: ClosestPolygonPoint | null = null;
    let bestDistSq = Infinity;
    let bestEdgeIndex = -1;
    let bestIsAtStartVertex = false;

    for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const b = nodes[(i + 1) % nodes.length];
        const cp = closestPointOnSegment(px, py, a.x, a.y, b.x, b.y);
        if (cp.distSq < bestDistSq) {
            bestDistSq = cp.distSq;
            bestEdgeIndex = i;
            bestIsAtStartVertex = Math.abs(cp.x - a.x) < EPSILON && Math.abs(cp.y - a.y) < EPSILON;
            best = { x: cp.x, y: cp.y, dist: Math.sqrt(cp.distSq), nx: 0, ny: 0 };
        }
    }

    if (!best) {
        // Degenerate (empty) polygon - shouldn't happen since callers only use
        // this for areas with >2 nodes, but fall back to the query point itself.
        return { x: px, y: py, dist: 0, nx: 0, ny: 0 };
    }

    const normalA = edgeOutwardNormal(nodes, bestEdgeIndex, ccw);
    // If the closest point is essentially on a vertex, blend with the
    // adjacent edge's normal so corners push out along the angle bisector
    // instead of snapping to one edge's normal arbitrarily.
    let nx = normalA.nx;
    let ny = normalA.ny;
    const prevEdgeIndex = (bestEdgeIndex - 1 + nodes.length) % nodes.length;
    const nextEdgeIndex = (bestEdgeIndex + 1) % nodes.length;
    const closeToEnd = Math.abs(best.x - nodes[(bestEdgeIndex + 1) % nodes.length].x) < EPSILON &&
        Math.abs(best.y - nodes[(bestEdgeIndex + 1) % nodes.length].y) < EPSILON;

    if (bestIsAtStartVertex) {
        const normalB = edgeOutwardNormal(nodes, prevEdgeIndex, ccw);
        nx = normalA.nx + normalB.nx;
        ny = normalA.ny + normalB.ny;
    } else if (closeToEnd) {
        const normalB = edgeOutwardNormal(nodes, nextEdgeIndex, ccw);
        nx = normalA.nx + normalB.nx;
        ny = normalA.ny + normalB.ny;
    }

    const len = Math.sqrt(nx * nx + ny * ny) || 1;
    best.nx = nx / len;
    best.ny = ny / len;
    return best;
}

// Segment (x1,y1)-(x2,y2) vs a single segment, parametric intersection test.
export function lineIntersectsLine(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number): boolean {
    const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

    if (denom === 0) {
        const crossProduct = (x1 - x3) * (y2 - y1) - (y1 - y3) * (x2 - x1);
        if (crossProduct !== 0) {
            return false;
        }
        const overlapping = (Math.min(x1, x2) <= Math.max(x3, x4) && Math.min(x3, x4) <= Math.max(x1, x2)) &&
            (Math.min(y1, y2) <= Math.max(y3, y4) && Math.min(y3, y4) <= Math.max(y1, y2));
        return overlapping;
    }

    const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;
    const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denom;

    return ua >= 0 && ua <= 1 && ub >= 0 && ub <= 1;
}

// Segment vs every edge of the polygon.
export function lineIntersectsPolygon(x1: number, y1: number, x2: number, y2: number, nodes: AreaNode[]): boolean {
    for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const b = nodes[(i + 1) % nodes.length];
        if (lineIntersectsLine(x1, y1, x2, y2, a.x, a.y, b.x, b.y)) {
            return true;
        }
    }
    return false;
}

export function polygonBounds(nodes: AreaNode[]): Bounds {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
        if (n.x < minX) minX = n.x;
        if (n.y < minY) minY = n.y;
        if (n.x > maxX) maxX = n.x;
        if (n.y > maxY) maxY = n.y;
    }
    return { minX, minY, maxX, maxY };
}

// Detects the common case of an unedited, axis-aligned 4-node rectangle
// (the shape every area starts as when created via click-drag in the editor).
// Used as a fast path so plain rectangular walls keep behaving exactly like
// they did before this polygon rework (important for narrow corridor walls,
// where general polygon push-out can jitter between two close parallel edges).
export function isAxisAlignedRect(nodes: AreaNode[]): boolean {
    if (nodes.length !== 4) {
        return false;
    }
    const xs = new Set(nodes.map(n => Math.round(n.x * 1000)));
    const ys = new Set(nodes.map(n => Math.round(n.y * 1000)));
    return xs.size === 2 && ys.size === 2;
}
